import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import { useId, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useInfiniteSocialMediaPosts, useUserQuery } from '../../../../queries';
import { filterCancelledSocialPosts } from '../../../../utils/socialMediaFiltering';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { Select, SelectCheckboxItem, SelectContent, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { bs } from '../../../styles/styles';
import { Portal } from '../../Portal';
import { UserSelectModal } from '../../UserSelectModal';
import { SocialMediaReportingTableRow } from './presentation';
import { SocialMediaReportingTable } from './SocialMediaReportingTable';
import { styleSheet } from './styles';
import { allReportStatusValues, mapReportPostStatusToPostStatus } from './UserSocialReportingTable';

interface IProps {
	impersonationContext: Api.IImpersonationContext;
	scrollToBottomPortalId: string;
}

const _AdminSocialMediaReportingTable = ({ impersonationContext, scrollToBottomPortalId }: IProps) => {
	const id = useId();
	const userSession = useUserSession();
	const [status, setStatus] = useState<Api.ReportPostStatus[]>(allReportStatusValues);
	const [userId, setUserId] = useState<string | null>(null);
	const [showingSentByModal, setShowingSentByModal] = useState(false);
	const userQuery = useUserQuery({
		enabled: Boolean(userId),
		id: userId,
		impersonationContext,
	});
	const postStatus = mapReportPostStatusToPostStatus(status);
	let senderLabel = '...';
	if (!userId) {
		senderLabel = 'All Employees';
	} else if (userQuery.data) {
		senderLabel = getDisplayName(userQuery.data);
	}
	const isCustomFilter = status.length !== allReportStatusValues.length || Boolean(userId);
	const socialMediaPostsQuery = useInfiniteSocialMediaPosts({
		impersonationContext,
		selectedPostStatus: postStatus,
		meta: {
			errorLogging: {
				category: 'SocialMediaReportingTable',
				action: 'GetSocialReports-Error',
			},
		},
		selectedUserId: userId,
	});
	const handleStatusChanged = (value: Api.ReportPostStatus[]) => {
		if (value.length === 0) {
			setStatus(allReportStatusValues);
			return;
		}
		setStatus(value);
	};
	const onClearFilters = () => {
		setStatus(allReportStatusValues);
		setUserId(null);
	};

	return (
		<div>
			<div className={css(styleSheet.filter)}>
				{'Filter By: '}
				<Select multiple value={status} onValueChange={handleStatusChanged}>
					<SelectTrigger styles={[bs.w60]}>
						<SelectValue>{status.length === allReportStatusValues.length ? 'All' : status.join(', ')}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{allReportStatusValues.map(value => {
							return (
								<SelectCheckboxItem key={`${id}-status-${value}`} value={value}>
									{value}
								</SelectCheckboxItem>
							);
						})}
					</SelectContent>
				</Select>
				<Dropdown>
					<DropdownSelectTrigger styles={[bs.w60]}>Sent by {senderLabel}</DropdownSelectTrigger>
					<DropdownContent>
						<DropdownItem
							onClick={() => {
								if (userId != null) {
									setUserId(null);
								}
							}}
						>
							All Employees
						</DropdownItem>
						<DropdownItem onClick={() => setShowingSentByModal(true)}>Select an Employee</DropdownItem>
					</DropdownContent>
				</Dropdown>
				{isCustomFilter ? (
					<Button variant='secondary' onClick={onClearFilters}>
						Clear filters
					</Button>
				) : null}
			</div>
			<SocialMediaReportingTable postStatus={postStatus} userId={userId}>
				{socialMediaPostsQuery.status === 'success'
					? socialMediaPostsQuery.data.pages.map(group => {
							const isExplicitlyCancelledFilter = postStatus?.includes(Api.PostStatus.Cancelled);
							const filteredPosts = filterCancelledSocialPosts(group.values, isExplicitlyCancelledFilter);
							return filteredPosts.map(item => {
								const itemVm = new Api.SocialMediaPostReportViewModel(userSession, item).impersonate(
									impersonationContext
								);
								return (
									<SocialMediaReportingTableRow
										key={itemVm.id}
										report={itemVm}
										impersonationContext={impersonationContext}
									/>
								);
							});
						})
					: null}
			</SocialMediaReportingTable>
			{socialMediaPostsQuery.hasNextPage && !socialMediaPostsQuery.isFetchingNextPage ? (
				<Portal destination={scrollToBottomPortalId}>
					<Waypoint
						bottomOffset='-200px'
						onEnter={() => {
							socialMediaPostsQuery.fetchNextPage();
						}}
					/>
				</Portal>
			) : null}
			<UserSelectModal
				modalProps={{
					isOpen: showingSentByModal,
					onRequestClose: (user?: Api.UserViewModel, cancel?: boolean) => {
						setShowingSentByModal(false);
						if (user && !cancel) {
							setUserId(user.id);
						}
					},
				}}
				impersonationContext={impersonationContext}
				title='Select an Employee'
			/>
		</div>
	);
};

export const AdminSocialMediaReportingTable = inject(ImpersonationContextKey)(_AdminSocialMediaReportingTable);
